'use client';

import { useEffect, useState } from 'react';
import { FaHeart, FaMapMarkerAlt, FaClock, FaBuilding, FaSpinner } from 'react-icons/fa';
import { useRouter } from 'next/navigation';

interface JobCardProps {
  onJobClick: (job: any) => void;
  searchQuery?: string;
  standalone?: boolean; // If true, shows individual card; if false, shows grid of cards
  jobData?: any; // For standalone usage with pre-fetched data
}

interface Job {
  _id?: string;
  companyLogo_userimage?: string;
  companyName: string;
  createdAt: number;
  taskTitle: string;
  jobDescription: string;
  location: string;
  category?: string;
  taskersNeeded: number;
  duration: string;
  skills: string[];
  favorite: boolean;
  bidAvailable: boolean;
  payment: string;
}

export default function JobCard({ onJobClick, searchQuery = '', standalone = false, jobData }: JobCardProps) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const router = useRouter();

  useEffect(() => {
    if (jobData && standalone) {
      // Use provided job data for standalone card
      setJobs([jobData]);
      setLoading(false);
      return;
    }

    if (!standalone) {
      // Fetch multiple jobs for grid view
      const fetchJobs = async () => {
        try {
          setLoading(true);
          const response = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/tasks/random`);
          if (!response.ok) throw new Error('Failed to fetch jobs');
          const data = await response.json();
          setJobs(data);
        } catch (error) {
          setError("Error fetching jobs: " + (error instanceof Error ? error.message : "Unknown error"));
          console.error("Error fetching jobs:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchJobs();
    }
  }, [standalone, jobData]);

  const formatDate = (timestamp: number) => {
    const jobDate = new Date(timestamp);
    const today = new Date();

    if (
      jobDate.getDate() === today.getDate() &&
      jobDate.getMonth() === today.getMonth() &&
      jobDate.getFullYear() === today.getFullYear()
    ) {
      return 'Today';
    }

    return jobDate.toLocaleDateString();
  };

  const filteredJobs = jobs.filter(job =>
    job.taskTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.jobDescription.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <FaSpinner className="animate-spin text-purple-600 text-4xl" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-8 mt-8">
        <div className="text-red-500 mb-4">{error}</div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // No jobs found
  if (filteredJobs.length === 0 && !standalone) {
    return (
      <div className="col-span-full text-center py-8">
        <div className="text-gray-500 text-lg mb-2">No jobs found</div>
        {searchQuery && (
          <p className="text-sm text-gray-400">Try adjusting your search query</p>
        )}
      </div>
    );
  }

  // Render individual job card
  const renderJobCard = (job: Job) => {
    // Safely access payment with fallback
    const paymentAmount = job.payment ? `₦${job.payment}` : 'Negotiable';
    const category = job.category || job.skills?.[0] || 'Task';

    return (
      <div 
        className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition duration-300 cursor-pointer relative"
        onClick={() => onJobClick(job)}
        key={job._id}
      >
        <div className="p-6">
          {/* Header with category and price */}
          <div className="flex justify-between items-start mb-4">
            <div>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {category}
              </span>
            </div>
            <span className="text-2xl font-bold text-gray-900">{paymentAmount}</span>
          </div>

          {/* Heart Icon */}
          <div className="absolute top-4 z-10">
            <button
              className={`text-xl ${
                job.favorite ? 'text-red-500' : 'text-gray-400'
              } hover:text-red-500 focus:outline-none`}
              title={job.favorite ? 'Remove from favorites' : 'Add to favorites'}
              aria-label={job.favorite ? 'Remove from favorites' : 'Add to favorites'}
              onClick={(e) => {
                e.stopPropagation();
                // Add favorite toggle logic here
              }}
            >
              <FaHeart />
            </button>
          </div>

        
          {/* Job Title */}
          <h3 className="text-lg font-medium text-gray-900 mb-2">{job.taskTitle}</h3>

          {/* Job Description */}
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{job.jobDescription}</p>

          {/* Location */}
          <div className="flex items-center text-sm text-gray-500 mb-2">
            <FaMapMarkerAlt className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
            {job.location}
          </div>

          {/* Duration/Time */}
          <div className="flex items-center text-sm text-gray-500 mb-2">
            <FaClock className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
            {job.duration || 'Flexible'}
          </div>

          {/* Company Name */}
          <div className="flex items-center text-sm text-gray-500">
            <FaBuilding className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
            {job.companyName}
          </div>
        </div>
        
        {/* Footer with View Task button */}
        <div className="bg-gray-50 px-6 py-4">
          <button className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-300"
              onClick={(e) => {
                e.stopPropagation();
                router.push('/tasks'); // Navigate to tasks page
              }}
            >
            View Task
          </button>
        </div>
      </div>
    );
  };

  // Return appropriate layout based on usage
  if (standalone) {
    // Single card mode
    return filteredJobs.length > 0 ? renderJobCard(filteredJobs[0]) : null;
  } else {
    // Grid mode
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredJobs.map((job) => renderJobCard(job))}
      </div>
    );
  }
}